import { useEffect, useRef, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { Input, DatePicker, Select } from "antd";
import { Search, Filter, ArrowUpDown, Calendar } from "lucide-react";
import { FiChevronDown } from "react-icons/fi";
import AppointmentFilterSlider from "../AppointmentFilterSlider";
import dayjs from "dayjs";

// Constants
const STATUS_OPTIONS = [
  { value: "all", label: "All" },
  { value: "UPCOMING", label: "Upcoming" },
  { value: "COMPLETED", label: "Completed" },
  { value: "CANCELED", label: "Cancelled" },
  // { value: "MISSED", label: "Missed" },
  { value: "IN_COMPLETE", label: "Incomplete" },
  { value: "COUNSELOR_UNAVAILABLE", label: "Counsellor Unavailable" },
  { value: "STUDENT_MISSED", label: "Student Missed" },
  { value: "COUNSELLOR_MISSED", label: "Counsellor Missed" },
  { value: "MISSED_BY_BOTH", label: "Missed By Both" }
];

// Reusable class constants
const CLASSES = {
  CONTAINER: 'bg-white rounded-xl p-2 w-full',
  TITLE: 'font-poppins font-semibold text-base text-[#171717] leading-6 mb-0',
  SUBTITLE: 'font-poppins font-normal text-xs text-[#64748B] leading-[18px]',
  SEARCH_BOX: 'relative w-[250px] max-md:w-full',
  SEARCH_INPUT: 'bg-white border-[1px] border-solid border-[#E2E8F0] rounded-xl shadow-sm h-9 pl-9 text-sm text-[#171717] placeholder:text-[#94A3B8] font-poppins focus:ring-2 focus:ring-[#B3D7FF]',
  SEARCH_ICON: 'absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8] pointer-events-none',
  ACTION_BTN: 'flex items-center gap-2 h-9 px-[14px] bg-white text-[#171717] text-[13px] font-semibold font-poppins border-[1px] border-solid border-[#E2E8F0] rounded-xl shadow-sm hover:bg-[#F8FAFC] transition',
  DROPDOWN_MENU: 'absolute mt-2 bg-white border-[1px] border-solid border-[#E2E8F0] rounded-xl shadow-md z-10',
  DROPDOWN_ITEM: 'px-4 py-2 text-sm cursor-pointer hover:bg-[#F8FAFC]',
  ACTIONS_WRAPPER: 'flex items-center gap-3 max-md:flex-wrap max-md:gap-2'
};

function AppointmentsControl({ onFiltersChange, onSearch, onStatusFilter, onDateFilter, currentStatus, onSortChange, appliedFilters }) {
  const [open, setOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [currentSort, setCurrentSort] = useState(null);
  const dropdownRef = useRef();

  // Use currentStatus directly from props, with fallback
  const selectedStatus = currentStatus || STATUS_OPTIONS[0].value;

  // Close menus on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
        setSortOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleStatusChange = useCallback((value) => {
    setOpen(false);
    if (onStatusFilter) onStatusFilter(value);
  }, [onStatusFilter]);

  const handleDateChange = useCallback((date, dateString) => {
    setSelectedDate(date);
    if (onDateFilter) onDateFilter(dateString);
  }, [onDateFilter]);

  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    setSearchValue(value);
    if (onSearch) onSearch(value);
  }, [onSearch]);

  const handleFilterToggle = useCallback(() => {
    setIsFilterOpen(true);
  }, []);

  const handleFilterClose = useCallback(() => {
    setIsFilterOpen(false);
  }, []);

  const handleFiltersApply = useCallback((filters) => {
    if (onFiltersChange) onFiltersChange(filters);
  }, [onFiltersChange]);

  const handleFiltersClear = useCallback(() => {
    if (onFiltersChange) onFiltersChange(null);
  }, [onFiltersChange]);

  const handleSortChange = useCallback((field, order) => {
    setCurrentSort({ field, order });
    if (onSortChange) onSortChange({ field, order });
    setSortOpen(false);
  }, [onSortChange]);

  const handleClearSort = useCallback(() => {
    setCurrentSort(null);
    if (onSortChange) onSortChange(null);
    setSortOpen(false);
  }, [onSortChange]);

  const isCurrentSort = useCallback((field, order) => {
    return currentSort?.field === field && currentSort?.order === order;
  }, [currentSort]);

  return (
    <div className={CLASSES.CONTAINER}>
      <div className="flex justify-between items-center w-full gap-4 max-lg:flex-col max-lg:items-start">

        {/* Title & Subtitle */}
        <div className="flex flex-col gap-1">
          <h4 className={CLASSES.TITLE}>All Appointments</h4>
          <span className={CLASSES.SUBTITLE}>
            View and manage all appointments scheduled across the platform.
          </span>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3 max-lg:flex-wrap max-lg:gap-2 max-lg:w-full">

          {/* Search */}
          <div className="relative w-[250px] max-lg:w-full max-lg:order-1">
            <Input
              placeholder="Search by Member Name / Practitioner Name"
              className={CLASSES.SEARCH_INPUT}
              aria-label="Search appointments"
              value={searchValue}
              onChange={handleSearchChange}
            />
            <Search size={16} className={CLASSES.SEARCH_ICON} />
          </div>

          {/* Status Dropdown */}
          <Select
            value={selectedStatus}
            options={STATUS_OPTIONS}
            onChange={handleStatusChange}
            className="w-[220px] max-lg:w-full max-lg:order-2 h-9 font-poppins text-sm font-normal text-[#171717]"
            size="middle"
            aria-label="Filter by status"
            dropdownStyle={{
              maxHeight: '300px',
              width: '220px'
            }}
            listHeight={280}
            placeholder="Select Status"
            showSearch={false}
          />

          {/* Date Picker */}
          <DatePicker
            onChange={handleDateChange}
            placeholder="Select Date"
            className="h-9 rounded-xl border-[1px] border-solid border-[#E2E8F0] bg-white shadow-sm px-3 text-[13px] w-36 max-lg:w-full max-lg:order-3 font-poppins"
            value={selectedDate}
            aria-label="Select date"
            suffixIcon={<Calendar size={16} className="text-[#64748B]" />}
          />

          {/* Filter */}
          <button
            className={`${CLASSES.ACTION_BTN} max-lg:flex-1 max-lg:justify-center max-lg:order-4`}
            onClick={handleFilterToggle}
            aria-label="Open advanced filters"
          >
            Filter
            <Filter size={16} className="text-[#2563EB]" />
          </button>

          {/* Sort Dropdown */}
          <div className="relative inline-block max-lg:order-5" ref={dropdownRef}>
            <button
              onClick={() => setSortOpen(!sortOpen)}
              className="relative flex items-center justify-center w-9 h-9 border-[1px] border-solid border-[#E2E8F0] bg-white rounded-xl shadow-sm hover:bg-[#F8FAFC] transition"
              aria-label="Sort appointments"
            >
              <ArrowUpDown size={16} className="text-[#171717]" />
              {currentSort && (
                <span className="absolute -top-1 -right-1 bg-[#2563EB] text-white text-[10px] font-bold w-4 h-4 flex items-center justify-center rounded-full">
                  1
                </span>
              )}
            </button>
            {sortOpen && (
              <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-2xl shadow-lg border-[1px] border-solid border-[#E2E8F0] py-2 z-50 font-poppins text-sm text-[#334155]">
                {/* Clear Sort Option */}
                {currentSort && (
                  <div
                    className="px-4 py-3 hover:bg-[#F8FAFC] cursor-pointer border-b-[1px] border-solid border-x-0 border-t-0 border-[#F1F5F9] text-[#64748B] font-medium"
                    onClick={handleClearSort}
                  >
                    ✕ Clear Sorting
                  </div>
                )}

                <div
                  className={`px-4 py-3 cursor-pointer ${
                    isCurrentSort("memberName", "asc")
                      ? "bg-[#EFF6FF] text-[#2563EB] font-semibold"
                      : "hover:bg-[#F8FAFC]"
                  }`}
                  onClick={() => handleSortChange("memberName", "asc")}
                >
                  {isCurrentSort("memberName", "asc") && "✓ "} Sort by Name (A-Z)
                </div>
                <div
                  className={`px-4 py-3 cursor-pointer ${
                    isCurrentSort("memberName", "desc")
                      ? "bg-[#EFF6FF] text-[#2563EB] font-semibold"
                      : "hover:bg-[#F8FAFC]"
                  }`}
                  onClick={() => handleSortChange("memberName", "desc")}
                >
                  {isCurrentSort("memberName", "desc") && "✓ "} Sort by Name (Z-A)
                </div>
              </div>
            )}
          </div>
        </div>

      </div>

      {/* Filter Slider */}
      <AppointmentFilterSlider
        isOpen={isFilterOpen}
        onClose={handleFilterClose}
        onApplyFilters={handleFiltersApply}
        onClearAll={handleFiltersClear}
        currentFilters={appliedFilters}
      />
    </div>
  );
}

AppointmentsControl.propTypes = {
  onFiltersChange: PropTypes.func,
  onSearch: PropTypes.func,
  onStatusFilter: PropTypes.func,
  onDateFilter: PropTypes.func,
  currentStatus: PropTypes.string,
  onSortChange: PropTypes.func,
  appliedFilters: PropTypes.object
};

AppointmentsControl.defaultProps = {
  onFiltersChange: null,
  onSearch: null,
  onStatusFilter: null,
  onDateFilter: null,
  currentStatus: null,
  onSortChange: null,
  appliedFilters: null
};

export default AppointmentsControl;
